import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import { Config } from "@eagerdog/config";
import { Constants, isUKCConformationBISTile, isUKCConformationShow } from "@eagerdog/constants";
import { assignMissingDayOrders, judgeIdentityKey, reassignConcurrentClass, reorderConcurrentClass, shiftConcurrentClass } from "@eagerdog/judge-schedule-util";
import { IEvent, IRunningOrder, IShow, RunningOrderDogClassDto } from "@eagerdog/interfaces";
import moment from "moment-timezone";

import { apiService } from "src/services";
import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import Confirm from "src/components/Confirm/Confirm";
import { toast } from "src/components/Toast/ToastManager";
import { AddRunningOrderJudge } from "../JudgeSchedulesTab/AddRunningOrderJudge";
import { EditRunningOrderDogClass, IEditRunningOrderDogClassValues } from "../JudgeSchedulesTab/EditRunningOrderDogClass";
import { MoveClassToRing } from "../JudgeSchedulesTab/MoveClassToRing";

import { useJudgeScheduleBuilderData } from "./useJudgeScheduleBuilderData";
import { buildScheduleModel, isClassRowVisible, sameLocalDay, ScheduleResource } from "./timeline/buildModel";
import { insertionIndexFromDrop, reorderClassToRow, reorderJudges } from "./timeline/dropToSequence";
import { formatMinutes, isBreak, isJuniorShowmanship } from "./timeline/labels";
import {
  addBreakTile,
  deleteJudgeTile,
  groupClasses,
  mergeClassSplits,
  splitClassTile,
  moveJudgeToRing,
  nextRingName,
  nextRingNameForShows,
  planClassMove,
  removeTile,
  replaceRingOrder,
  reverseRing,
  setTileLength,
  shiftJudgeByMinutes,
  shiftTileByMinutes,
  snapBreakMinutes,
  transferClassToRing,
  updateBreakTile,
} from "./shared/judgeScheduleMutations";
import BreakForm from "./components/BreakForm";
import MoveToJudgePicker from "./components/MoveToJudgePicker";
import RingTrialPicker, { PickerRing } from "./components/RingTrialPicker";
import styles from "./JudgeScheduleBuilder.module.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  onUpdate: (updatedShow: IShow) => void;
  onJudgeScheduleChange?: () => void;
  onClose: () => void;
}

type FormState =
  | { kind: "addJudge"; showId: string; ring: string }
  | { kind: "editJudge"; showId: string; ring: string; judgeIndex: number }
  | { kind: "editClass"; showId: string; ring: string; judgeIndex: number; classIndex: number }
  | { kind: "moveClass"; showId: string; ring: string; judgeIndex: number; classIndex: number }
  | { kind: "moveToJudge"; resourceId: string }
  | { kind: "moveJudge"; resourceId: string }
  | { kind: "pick"; purpose: "addJudge" | "addRing" | "reset" | "reverse" }
  | { kind: "merge"; showId: string; ring: string; judgeIndex: number; classIndex: number }
  | { kind: "addBreak"; showId: string; ring: string; judgeIndex: number; atIndex: number }
  | { kind: "editBreak"; showId: string; ring: string; judgeIndex: number; classIndex: number }
  | null;

interface GridBreakMenu {
  x: number;
  y: number;
  showId: string;
  ring: string;
  judgeIndex: number;
  atIndex: number;
  /** Set when the click landed on an existing break, so it can be edited or removed. */
  breakClassIndex?: number;
  /** Set when that break is a UKC Conformation BIS block. Removing it hides the block for this show. */
  bisClass?: string;
}

/** Left-side trial, ring and judge rows take the same colors as their bars. */
function rowCellClassNames(arg: { resource?: { extendedProps: Record<string, any> } }): string[] {
  const { kind, overCapacity } = arg.resource?.extendedProps || {};
  if (kind !== "trial" && kind !== "ring" && kind !== "judge") return [];
  return [`jsb-row-${kind}`, kind === "judge" && overCapacity ? "jsb-row-over" : ""].filter(Boolean);
}

/** When a bar is narrower than its label, draw the label just to the right of the bar. */
function placeOutsideLabel(el: HTMLElement) {
  const title = el.querySelector(".fc-event-title") as HTMLElement | null;
  const text = title?.textContent?.trim();
  if (!title || !text) {
    el.classList.remove("jsb-label-outside");
    return;
  }
  const barWidth = el.clientWidth;
  if (barWidth <= 0) return;
  const probe = document.createElement("span");
  const font = getComputedStyle(title).font;
  probe.textContent = text;
  probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${font};`;
  document.body.appendChild(probe);
  const textWidth = probe.getBoundingClientRect().width;
  probe.remove();
  el.classList.toggle("jsb-label-outside", textWidth + 14 > barWidth);
}

function slotAtX(root: Element, clientX: number): HTMLElement | null {
  const cells = Array.from(root.querySelectorAll(".fc-timeline-slots td[data-date]")) as HTMLElement[];
  return cells.find((cell) => {
    const rect = cell.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right;
  }) || null;
}

function clickTimeMs(slot: HTMLElement, clientX: number, timezone: string): number | null {
  const raw = slot.getAttribute("data-date");
  if (!raw) return null;
  const start = moment.tz(raw, timezone);
  if (!start.isValid()) return null;
  const rect = slot.getBoundingClientRect();
  const fraction = rect.width > 0 ? Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) : 0;
  const nextRaw = (slot.nextElementSibling as HTMLElement | null)?.getAttribute?.("data-date");
  const next = nextRaw ? moment.tz(nextRaw, timezone) : start.clone().add(15, "minutes");
  const click = start.clone().add(Math.max(0, next.diff(start)) * fraction, "ms");
  click.minutes(Math.round(click.minutes() / 5) * 5).seconds(0).milliseconds(0);
  return click.valueOf();
}

const JudgeScheduleBuilder: React.FC<IProps> = ({ event, show: anchor, onUpdate, onJudgeScheduleChange, onClose }) => {
  const { shows, setShows, loading, loadError, reload } = useJudgeScheduleBuilderData(event, anchor._id);
  const { isShown, toggle } = useModal();
  const [form, setForm] = useState<FormState>(null);
  const [viewDate, setViewDate] = useState(() => moment(anchor.show_date).format("YYYY-MM-DD"));
  const [hideEmpty, setHideEmpty] = useState(false);
  const [concurrent, setConcurrent] = useState(!!event.concurrent_schedule);
  const [grouping, setGrouping] = useState(false);
  const [groupedIds, setGroupedIds] = useState<string[]>([]);
  const [selectedJudgeId, setSelectedJudgeId] = useState<string>("");
  const [narrow, setNarrow] = useState(() => window.innerWidth < 1100);
  const [saving, setSaving] = useState(false);
  const [gridMenu, setGridMenu] = useState<GridBreakMenu | null>(null);
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; resourceId: string; place: "button" | "cursor" } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ message: string; run: () => void } | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);
  const collapsedOnce = useRef(false);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1100);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    calendarRef.current?.getApi().gotoDate(viewDate);
  }, [viewDate]);

  useEffect(() => {
    if (!gridMenu && !rowMenu) return;
    const close = () => {
      setGridMenu(null);
      setRowMenu(null);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [gridMenu, rowMenu]);

  const timezone = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const visibleShows = shows.filter((item) => moment(item.show_date).format("YYYY-MM-DD") === viewDate);
  const scheduleEvent = useMemo(
    () => ({ ...event, concurrent_schedule: concurrent }),
    [event, concurrent]
  );

  const model = useMemo(
    () => buildScheduleModel(visibleShows, shows, scheduleEvent, hideEmpty),
    [visibleShows, shows, scheduleEvent, hideEmpty]
  );

  useEffect(() => {
    const root = document.querySelector(".jsb-calendar");
    if (!root) return;
    const measure = () => root.querySelectorAll(".fc-timeline-event").forEach((el) => placeOutsideLabel(el as HTMLElement));
    const frame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [model.events, viewDate, loading, narrow]);

  useEffect(() => {
    if (loading || collapsedOnce.current || narrow) return;
    const timer = window.setTimeout(() => {
      const root = document.querySelector(".jsb-calendar");
      root?.querySelectorAll("tr[data-resource-id^='judge:'] .fc-icon-minus-square").forEach((icon) => {
        (icon.parentElement as HTMLElement | null)?.click();
      });
      collapsedOnce.current = true;
    }, 50);
    return () => window.clearTimeout(timer);
  }, [loading, model.resources.length, narrow]);

  const dates = Array.from(new Set(shows.map((item) => moment(item.show_date).format("YYYY-MM-DD")))).sort();
  const anchorShow = shows.find((item) => item._id === anchor._id) || anchor;
  const findShow = (showId: string) => shows.find((item) => item._id === showId);
  const ringOf = (show: IShow, ring: string) => (show.rings || []).find((item) => item.ring === ring);
  const classCanMove = (resource?: ScheduleResource) => {
    if (!resource || resource.extendedProps.kind !== "class") return false;
    const { showId, ring = "", judgeIndex, classIndex } = resource.extendedProps;
    if (judgeIndex == null || classIndex == null) return false;
    const show = findShow(showId);
    const tile = show && tileAt(show, ring, judgeIndex, classIndex);
    return !!tile && !isJuniorShowmanship(tile);
  };

  const moveJudgeChoices = (from: ScheduleResource) => {
    const judges = model.resources.filter((item) => item.extendedProps.kind === "judge");
    const others = judges.filter((item) => {
      if (concurrent) {
        return !(item.extendedProps.judgeKey === from.extendedProps.judgeKey && item.extendedProps.ring === from.extendedProps.ring);
      }
      if (item.extendedProps.showId !== from.extendedProps.showId) return false;
      return !(item.extendedProps.ring === from.extendedProps.ring && item.extendedProps.judgeIndex === from.extendedProps.judgeIndex);
    });
    const rings = new Set(others.map((item) => item.extendedProps.ring).concat(from.extendedProps.ring));
    return others.map((item) => ({
      id: item.id,
      label: rings.size > 1 ? `${item.extendedProps.ring} · ${item.title}` : item.title,
    }));
  };

  const moveRingChoices = (from: ScheduleResource): PickerRing[] => {
    const current = from.extendedProps.ring || "";
    const names = concurrent
      ? Array.from(new Set(visibleShows.flatMap((show) => (show.rings || []).map((ring) => ring.ring))))
      : (findShow(from.extendedProps.showId)?.rings || []).map((ring) => ring.ring);
    return names.filter((ring) => ring && ring !== current).map((ring) => ({
      showId: from.extendedProps.showId,
      showLabel: "",
      ring,
    }));
  };

  const finishMoveJudge = async (resource: ScheduleResource, targetRing: string) => {
    const fromRing = resource.extendedProps.ring || "";
    if (!fromRing || fromRing === targetRing) return;
    if (concurrent && resource.extendedProps.judgeKey) {
      const judgeKey = resource.extendedProps.judgeKey;
      let movedAny = false;
      let skipped = false;
      const next = shows.map((show) => {
        if (!visibleShows.some((item) => item._id === show._id)) return show;
        const source = (show.rings || []).find((ring) => ring.ring === fromRing);
        const judgeIndex = (source?.running_order || []).findIndex((judge) => judgeIdentityKey(judge.judge_name, judge.judge_number) === judgeKey);
        if (judgeIndex < 0) return show;
        if (!(show.rings || []).some((ring) => ring.ring === targetRing)) {
          skipped = true;
          return show;
        }
        const moved = moveJudgeToRing(show, fromRing, judgeIndex, targetRing);
        if (moved.error) return show;
        movedAny = true;
        return moved.show;
      });
      if (!movedAny) {
        toast.show({ title: "Move Judge", content: "That ring isn't on this judge's trial.", duration: 6000, type: "fail" });
        return;
      }
      if (skipped) {
        toast.show({ title: "Move Judge", content: "Moved the judge on the trials that have that ring.", duration: 6000, type: "info" });
      }
      await saveShows(next);
      return;
    }
    const show = findShow(resource.extendedProps.showId);
    const judgeIndex = resource.extendedProps.judgeIndex;
    if (!show || judgeIndex == null) return;
    const moved = moveJudgeToRing(show, fromRing, judgeIndex, targetRing);
    if (moved.error) return;
    setSaving(true);
    try {
      for (const ringName of moved.rings) {
        const order = (moved.show.rings || []).find((ring) => ring.ring === ringName)?.running_order || [];
        await apiService.updateRingRunningOrder(event._id, show._id, ringName, order);
      }
      setShows((prev) => prev.map((item) => item._id === show._id ? moved.show : item));
      notify(moved.show);
    } catch (error) {
      fail("Move Judge", error, "Couldn't move that judge.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const tileAt = (show: IShow, ring: string, judgeIndex: number, classIndex: number) =>
    ringOf(show, ring)?.running_order?.[judgeIndex]?.dog_classes?.[classIndex];

  const notify = (updated: IShow) => {
    if (updated._id === anchor._id) onUpdate(updated);
    onJudgeScheduleChange?.();
  };

  const fail = (title: string, error: any, fallback: string) => {
    toast.show({ title, content: error?.response?.data?.message || fallback, duration: 8000, type: "fail" });
  };

  const saveShowStart = async (show: IShow, showDate: Date, rings: IShow["rings"]) => {
    const optimistic = { ...show, show_date: showDate, rings: rings || show.rings };
    setShows((prev) => prev.map((item) => item._id === show._id ? optimistic : item));
    setSaving(true);
    try {
      const { __v, updated_at, created_at, ...rest } = show as IShow & { __v?: number; updated_at?: string; created_at?: string };
      const updated = await apiService.updateShow(event._id, show._id, { ...rest, show_date: showDate, rings: optimistic.rings } as IShow);
      const merged = { ...optimistic, ...updated, show_date: showDate, rings: updated?.rings || optimistic.rings };
      setShows((prev) => prev.map((item) => item._id === show._id ? merged : item));
      notify(merged);
    } catch (error) {
      fail("Show Start", error, "Couldn't update the show start time.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  /**
   * UKC Conformation BIS blocks come back on every load unless the show records that the
   * club hid them, so removing one saves the ring and `hidden_bis_classes` in one update.
   */
  const saveBisBlocks = async (show: IShow, rings: IShow["rings"], hiddenBisClasses: string[], title: string) => {
    const optimistic = { ...show, rings, hidden_bis_classes: hiddenBisClasses };
    setShows((prev) => prev.map((item) => item._id === show._id ? optimistic : item));
    setSaving(true);
    try {
      const { __v, updated_at, created_at, ...rest } = show as IShow & { __v?: number; updated_at?: string; created_at?: string };
      const updated = await apiService.updateShow(event._id, show._id, { ...rest, rings, hidden_bis_classes: hiddenBisClasses } as IShow);
      const merged = { ...optimistic, ...updated, rings: updated?.rings || rings, hidden_bis_classes: updated?.hidden_bis_classes || hiddenBisClasses };
      setShows((prev) => prev.map((item) => item._id === show._id ? merged : item));
      notify(merged);
    } catch (error) {
      fail(title, error, "Couldn't update the schedule.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const hideBisBlock = (show: IShow, ring: string, judgeIndex: number, classIndex: number) => {
    const order = ringOf(show, ring)?.running_order || [];
    const tile = order[judgeIndex]?.dog_classes?.[classIndex];
    if (!tile?.bis_class) return;
    const hidden = Array.from(new Set([...(show.hidden_bis_classes || []), tile.bis_class]));
    const rings = replaceRingOrder(show, ring, removeTile(order, judgeIndex, classIndex)).rings;
    saveBisBlocks(show, rings, hidden, "Remove Block");
  };

  const restoreBisBlock = (show: IShow, ring: string, judgeIndex: number, atIndex: number | undefined, bisClass: string) => {
    const order = ringOf(show, ring)?.running_order || [];
    const before = order[judgeIndex]?.dog_classes?.length || 0;
    const insertAt = atIndex == null || atIndex >= before ? before : Math.max(0, atIndex);
    const next = addBreakTile(order, judgeIndex, atIndex, { name: bisClass, duration: Constants.ukc_conformation_bis_default_minutes });
    const inserted = next[judgeIndex]?.dog_classes[insertAt];
    if (inserted) inserted.bis_class = bisClass;
    const hidden = (show.hidden_bis_classes || []).filter((item) => item !== bisClass);
    saveBisBlocks(show, replaceRingOrder(show, ring, next).rings, hidden, "Restore Block");
  };

  const saveRingOffset = async (show: IShow, ring: string, minutes: number) => {
    const optimistic = {
      ...show,
      rings: (show.rings || []).map((item) => item.ring === ring ? { ...item, start_offset_minutes: minutes } : item),
    };
    setShows((prev) => prev.map((item) => item._id === show._id ? optimistic : item));
    setSaving(true);
    try {
      const updated = await apiService.updateRingStartOffset(event._id, show._id, ring, minutes);
      const rings = (updated?.rings || optimistic.rings || []).map((item) => (
        item.ring === ring ? { ...item, start_offset_minutes: item.start_offset_minutes ?? minutes } : item
      ));
      const merged = { ...show, ...(updated || {}), rings };
      setShows((prev) => prev.map((item) => item._id === show._id ? merged : item));
      notify(merged._id ? merged : optimistic);
    } catch (error) {
      fail("Ring Start", error, "Couldn't update the ring start time.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const saveRing = async (show: IShow, ring: string, runningOrder: IRunningOrder[]) => {
    const optimistic = replaceRingOrder(show, ring, runningOrder);
    setShows((prev) => prev.map((item) => item._id === show._id ? optimistic : item));
    setSaving(true);
    let attempt = 0;
    try {
      while (attempt < 3) {
        try {
          const updated = await apiService.updateRingRunningOrder(event._id, show._id, ring, runningOrder);
          const merged = updated?.rings ? updated : optimistic;
          setShows((prev) => prev.map((item) => item._id === show._id ? { ...item, ...merged, rings: merged.rings || optimistic.rings } : item));
          notify(merged._id ? merged : optimistic);
          return;
        } catch (error: any) {
          const message = error?.response?.data?.message || "";
          if (attempt < 2 && /Write conflict|yielding is disabled/i.test(message)) {
            attempt += 1;
            await new Promise((resolve) => setTimeout(resolve, 100 * (2 ** attempt)));
            continue;
          }
          throw error;
        }
      }
    } catch (error) {
      fail("Judge Schedule", error, "Couldn't save that change.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const applyEffects = (showId: string, effects: { scores?: { scoreIds?: string[]; filter?: Record<string, unknown>; judge_name: string; judge_number: string }; element?: { show_element: string; level?: string; section?: string; judge_name: string; judge_number: string } }) => {
    if (effects.scores) {
      const query = effects.scores.scoreIds ? { _id: { $in: effects.scores.scoreIds } } : effects.scores.filter;
      apiService.bulkUpdateScoreJudge(event._id, query, {
        judge_name: effects.scores.judge_name,
        judge_number: effects.scores.judge_number,
      }).catch((error) => fail("Move Class", error, "The class moved, but the entries' judge was not updated."));
    }
    if (effects.element) {
      apiService.updateShowElementJudge(event._id, showId, effects.element)
        .catch((error) => fail("Move Class", error, "The class moved, but the default judge was not updated."));
    }
  };

  const refuseMove = (reason: "break" | "junior" | "missing") => {
    if (reason === "break") {
      toast.show({ title: "Judge Breaks", content: "Right-click that judge's row in the time grid to add a break there.", duration: 8000, type: "fail" });
    } else if (reason === "junior") {
      toast.show({
        title: "Junior Showmanship",
        content: "Junior Showmanship classes cannot be moved between judges as they are tied to specific judge assignments in the registrations.",
        duration: 10000,
        type: "fail",
      });
    }
  };

  const visibleTiles = (show: IShow, ring: string, judgeIndex: number) => {
    const timeline = model.timelines.get(show._id);
    const judge = timeline?.rings.find((item) => item.ring === ring)?.judges.find((item) => item.judgeIndex === judgeIndex);
    const classes = ringOf(show, ring)?.running_order?.[judgeIndex]?.dog_classes || [];
    return classes.map((dogClass, index) => ({
      index,
      visible: isClassRowVisible(dogClass, hideEmpty, show),
      startMs: judge?.classes.find((slot) => slot.classIndex === index)?.start.getTime() || 0,
    }));
  };

  const saveShows = async (nextShows: IShow[]): Promise<boolean> => {
    setShows(nextShows);
    setSaving(true);
    try {
      for (const next of nextShows) {
        const prev = shows.find((item) => item._id === next._id);
        for (const ring of next.rings || []) {
          const prevRing = (prev?.rings || []).find((item) => item.ring === ring.ring);
          if (JSON.stringify(prevRing?.running_order || []) === JSON.stringify(ring.running_order || [])) continue;
          await apiService.updateRingRunningOrder(event._id, next._id, ring.ring, ring.running_order || []);
        }
        if (next._id === anchor._id) onUpdate(next);
      }
      onJudgeScheduleChange?.();
      return true;
    } catch (error) {
      fail("Judge Schedule", error, "Couldn't save that change.");
      reload();
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onEventAllow = (dropInfo: any, dragged: any) => {
    if (grouping) return false;
    const from = dragged.extendedProps || {};
    const target = dropInfo.resource?.extendedProps || {};
    const sameMergedJudge = concurrent && from.judgeKey && from.judgeKey === target.judgeKey && from.ring === target.ring;
    const crossShow = Boolean(from.showId && target.showId && from.showId !== target.showId);
    if (crossShow && !concurrent) return false;
    if (from.kind === "class" || from.kind === "break") {
      if (target.kind !== "judge" && target.kind !== "class" && target.kind !== "break") return false;
      const sameJudge = sameMergedJudge || (!concurrent && target.showId === from.showId && target.ring === from.ring && target.judgeIndex === from.judgeIndex);
      if (!sameJudge && from.kind === "break") return false;
      const show = findShow(from.showId);
      const tile = show && tileAt(show, from.ring, from.judgeIndex, from.classIndex);
      if (!sameJudge && tile && isJuniorShowmanship(tile)) return false;
      const draggedResourceId = dragged.getResources?.()?.[0]?.id;
      const sameRow = Boolean(dropInfo.resource) && dropInfo.resource.id === draggedResourceId;
      if (sameRow) {
        const proposedStart = dropInfo.start?.getTime?.() ?? 0;
        const proposedEnd = dropInfo.end?.getTime?.() ?? proposedStart;
        const proposedMinutes = (proposedEnd - proposedStart) / 60000;
        if (from.kind === "class" && proposedMinutes + 0.05 < (from.minMinutes || 0)) return false;
        if (from.kind === "break" && proposedMinutes + 0.05 < 5) return false;
        const slackMs = (from.slackBeforeMinutes || 0) * 60000;
        if (from.startMs != null && proposedStart < from.startMs - slackMs - 500) return false;
      }
      return true;
    }
    if (from.kind === "trial" || from.kind === "ring") {
      if (crossShow) return false;
      const proposedStart = dropInfo.start?.getTime?.() ?? 0;
      const slackMs = (from.slackBeforeMinutes || 0) * 60000;
      if (from.slackBeforeMinutes != null && from.startMs != null && proposedStart < from.startMs - slackMs - 500) return false;
      return true;
    }
    if (from.kind === "judge") {
      const draggedResourceId = dragged.getResources?.()?.[0]?.id;
      if (dropInfo.resource?.id === draggedResourceId) {
        const proposedStart = dropInfo.start?.getTime?.() ?? 0;
        const slackMs = (from.slackBeforeMinutes || 0) * 60000;
        if (from.startMs != null && proposedStart < from.startMs - slackMs - 500) return false;
      }
      return target.kind === "ring" || target.kind === "judge" || target.kind === "class" || target.kind === "break";
    }
    return false;
  };

  const onEventDrop = async (info: any) => {
    const from = { ...(info.event.extendedProps || {}) };
    const target = info.newResource?.extendedProps || info.event.getResources?.()?.[0]?.extendedProps || {};
    const dropStart = info.event.start ? info.event.start.getTime() : 0;
    const oldStartMs = info.oldEvent?.start?.getTime?.() ?? from.startMs ?? dropStart;
    const sameResource = !info.newResource || info.newResource.id === info.oldResource?.id;
    info.revert();
    const show = findShow(from.showId);
    if (!show) return;
    const sourceOrder = ringOf(show, from.ring)?.running_order || [];
    const deltaMinutes = (dropStart - oldStartMs) / 60000;

    if (from.kind === "trial") {
      if (Math.abs(deltaMinutes) < 0.25) return;
      const nextDate = new Date(new Date(show.show_date).getTime() + (dropStart - oldStartMs));
      const rings = show.trailing_show
        ? (show.rings || []).map((ring) => ({
          ...ring,
          start_offset_minutes: Math.max(0, Math.round(((ring.start_offset_minutes || 0) + deltaMinutes) * 10) / 10),
        }))
        : show.rings;
      await saveShowStart(show, nextDate, rings);
      return;
    }

    if (from.kind === "ring") {
      const current = ringOf(show, from.ring)?.start_offset_minutes || 0;
      const nextOffset = Math.round((current + deltaMinutes) * 10) / 10;
      if (Math.abs(deltaMinutes) < 0.25 || nextOffset < -0.05) return;
      await saveRingOffset(show, from.ring, Math.max(0, nextOffset));
      return;
    }

    if (from.kind === "judge") {
      const destRing = target.ring;
      if (!destRing) return;
      if (destRing !== from.ring) {
        const moved = moveJudgeToRing(show, from.ring, from.judgeIndex, destRing);
        if (moved.error) return;
        setSaving(true);
        try {
          for (const ringName of moved.rings) {
            const order = (moved.show.rings || []).find((ring) => ring.ring === ringName)?.running_order || [];
            await apiService.updateRingRunningOrder(event._id, show._id, ringName, order);
          }
          setShows((prev) => prev.map((item) => item._id === show._id ? moved.show : item));
          notify(moved.show);
        } catch (error) {
          fail("Move Judge", error, "Couldn't move that judge.");
          reload();
        } finally {
          setSaving(false);
        }
        return;
      }
      const destIndex = target.judgeIndex;
      const sameJudge = sameResource || destIndex == null || destIndex === from.judgeIndex;
      if (sameJudge) {
        const shifted = shiftJudgeByMinutes(sourceOrder, from.judgeIndex, deltaMinutes);
        if (shifted) await saveRing(show, from.ring, shifted);
        return;
      }
      await saveRing(show, from.ring, reorderJudges(sourceOrder, from.judgeIndex, destIndex));
      return;
    }

    if (concurrent && from.judgeKey && (from.kind === "class" || from.kind === "break")) {
      const [judgeName, judgeNumber] = String(from.judgeKey).split("|");
      const sameMergedJudge = from.judgeKey === target.judgeKey && from.ring === target.ring;
      if (sameMergedJudge && sameResource) {
        const shifted = shiftConcurrentClass(visibleShows, from.ring, judgeName, judgeNumber, {
          showId: from.showId,
          judgeIndex: from.judgeIndex,
          classIndex: from.classIndex,
        }, (dropStart - oldStartMs) / 60000);
        if (shifted) await saveShows(shows.map((item) => shifted.find((next) => next._id === item._id) || item));
        return;
      }
      if (sameMergedJudge && (target.kind === "class" || target.kind === "break") && target.classIndex != null) {
        const reordered = reorderConcurrentClass(visibleShows, from.ring, judgeName, judgeNumber, {
          showId: from.showId,
          classIndex: from.classIndex,
        }, {
          showId: target.showId,
          classIndex: target.classIndex,
        });
        if (reordered) await saveShows(shows.map((item) => reordered.find((next) => next._id === item._id) || item));
        return;
      }
      if (!sameMergedJudge && target.judgeKey && target.ring && from.kind === "class") {
        const reassigned = reassignConcurrentClass(visibleShows, {
          showId: from.showId,
          ring: from.ring,
          judgeIndex: from.judgeIndex,
          classIndex: from.classIndex,
        }, {
          ring: target.ring,
          judgeKey: target.judgeKey,
        }, (target.kind === "class" || target.kind === "break") && target.classIndex != null ? {
          showId: target.showId,
          classIndex: target.classIndex,
        } : undefined);
        if (!reassigned.ok) {
          if (reassigned.reason === "ring") {
            toast.show({ title: "Move Class", content: "That trial does not use this ring.", duration: 6000, type: "fail" });
          } else if (reassigned.reason !== "missing") {
            refuseMove(reassigned.reason);
          }
          return;
        }
        const saved = await saveShows(shows.map((item) => reassigned.shows.find((next) => next._id === item._id) || item));
        if (saved) reassigned.effects.forEach((effect) => applyEffects(reassigned.showId, effect));
        return;
      }
    }

    if ((from.kind === "class" || from.kind === "break") && sameResource) {
      const shifted = shiftTileByMinutes(sourceOrder, from.judgeIndex, from.classIndex, (dropStart - oldStartMs) / 60000);
      if (shifted) await saveRing(show, from.ring, shifted);
      return;
    }

    const sameJudge = target.ring === from.ring && target.judgeIndex === from.judgeIndex;
    const onClassRow = (target.kind === "class" || target.kind === "break") && target.classIndex != null;
    if ((from.kind === "class" || from.kind === "break") && sameJudge && onClassRow) {
      const reordered = reorderClassToRow(sourceOrder, from.judgeIndex, from.classIndex, target.classIndex);
      if (reordered) await saveRing(show, from.ring, reordered);
      return;
    }

    const destRing = target.ring;
    const destJudge = target.judgeIndex;
    if (destRing == null || destJudge == null) return;
    const destOrder = ringOf(show, destRing)?.running_order || [];
    const insertAt = onClassRow
      ? target.classIndex
      : insertionIndexFromDrop(visibleTiles(show, destRing, destJudge), dropStart);

    if (destRing === from.ring) {
      const plan = planClassMove(sourceOrder, from.judgeIndex, from.classIndex, destJudge, insertAt, show._id);
      if (!plan.ok) {
        refuseMove(plan.reason);
        return;
      }
      await saveRing(show, from.ring, plan.runningOrder);
      applyEffects(show._id, plan.effects);
      return;
    }

    const transferred = transferClassToRing(sourceOrder, from.judgeIndex, from.classIndex, destOrder, destJudge, insertAt, show._id);
    if ("ok" in transferred && transferred.ok === false) {
      refuseMove(transferred.reason);
      return;
    }
    if (!("source" in transferred)) return;
    setSaving(true);
    try {
      await apiService.updateRingRunningOrder(event._id, show._id, from.ring, transferred.source);
      await apiService.updateRingRunningOrder(event._id, show._id, destRing, transferred.dest);
      const next = replaceRingOrder(replaceRingOrder(show, from.ring, transferred.source), destRing, transferred.dest);
      setShows((prev) => prev.map((item) => item._id === show._id ? next : item));
      notify(next);
      applyEffects(show._id, transferred.effects);
    } catch (error) {
      fail("Move Class", error, "Couldn't move that class.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const onEventResize = async (info: any) => {
    const props = { ...(info.event.extendedProps || {}) };
    const minutes = info.event.start && info.event.end ? (info.event.end.getTime() - info.event.start.getTime()) / 60000 : 0;
    info.revert();
    const show = findShow(props.showId);
    if (!show) return;
    const order = ringOf(show, props.ring)?.running_order || [];
    const tile = order[props.judgeIndex]?.dog_classes?.[props.classIndex];
    if (!tile) return;
    if (!isBreak(tile)) return;
    const next = setTileLength(order, props.judgeIndex, props.classIndex, snapBreakMinutes(minutes), 5);
    if (next) await saveRing(show, props.ring, next);
  };

  const openForm = (next: FormState) => {
    setForm(next);
    if (!isShown) toggle();
  };
  const closeForm = () => {
    setForm(null);
    if (isShown) toggle();
  };

  const pickerRings = (): PickerRing[] => visibleShows.flatMap((item) => (item.rings || []).map((ring) => ({
    showId: item._id,
    showLabel: `${item.show_type} ${item.show_name}`.trim(),
    ring: ring.ring,
  })));

  const judgeRings = (): PickerRing[] => {
    if (!concurrent) return pickerRings();
    const seen = new Set<string>();
    return pickerRings().filter((ring) => {
      if (seen.has(ring.ring)) return false;
      seen.add(ring.ring);
      return true;
    });
  };

  const pickerTrials = (): PickerRing[] => visibleShows.map((item) => ({
    showId: item._id,
    showLabel: `${item.show_type} ${item.show_name}`.trim(),
    ring: "",
  }));

  const withRing = (purpose: "addJudge" | "addRing" | "reset" | "reverse", run: (showId: string, ring: string) => void) => {
    if (purpose === "addRing") {
      const trials = pickerTrials();
      if (trials.length <= 1) {
        run(trials[0]?.showId || anchor._id, "");
        return;
      }
      openForm({ kind: "pick", purpose });
      return;
    }
    const rings = purpose === "addJudge" ? judgeRings() : pickerRings();
    if (rings.length === 0) {
      run(anchor._id, "Ring 1");
      return;
    }
    if (rings.length === 1) {
      run(rings[0].showId, rings[0].ring);
      return;
    }
    openForm({ kind: "pick", purpose });
  };

  const addRing = async (showId: string) => {
    const show = findShow(showId);
    if (!show) return;
    try {
      const updated = await apiService.addRingToShow(event._id, showId, nextRingName(show));
      setShows((prev) => prev.map((item) => item._id === showId ? updated : item));
      notify(updated);
    } catch (error) {
      fail("Add Ring", error, "Couldn't add a ring.");
    }
  };

  const addConcurrentRing = async () => {
    const targets = visibleShows.length > 0 ? visibleShows : [anchorShow];
    const name = nextRingNameForShows(targets);
    const missing = targets.filter((show) => !(show.rings || []).some((ring) => ring.ring === name));
    if (missing.length === 0) return;
    try {
      const updated = await Promise.all(missing.map((show) => apiService.addRingToShow(event._id, show._id, name)));
      setShows((prev) => prev.map((item) => updated.find((next) => next._id === item._id) || item));
      const anchorUpdate = updated.find((item) => item._id === anchor._id);
      if (anchorUpdate) notify(anchorUpdate);
    } catch (error) {
      fail("Add Ring", error, "Couldn't add a ring.");
      reload();
    }
  };

  const deleteRing = async (showId: string, ring: string) => {
    const show = findShow(showId);
    const target = show && ringOf(show, ring);
    if (!show || !target) return;
    if ((target.running_order || []).length > 0) {
      toast.show({ title: "Judge Schedules", content: `Can't remove ${ring}, the ring still has judges in it`, duration: 8000, type: "fail" });
      return;
    }
    try {
      const updated = await apiService.deleteRingFromShow(event._id, showId, ring);
      setShows((prev) => prev.map((item) => item._id === showId ? updated : item));
      notify(updated);
    } catch (error) {
      fail("Judge Schedules", error, "Couldn't remove that ring.");
    }
  };

  const addJudge = async (showId: string, ring: string, name: string, number: string) => {
    try {
      const updated = await apiService.addRingRunningOrder(event._id, showId, ring, { judge_name: name, judge_number: number, dog_classes: [] });
      setShows((prev) => prev.map((item) => item._id === showId ? updated : item));
      notify(updated);
      closeForm();
      reload();
    } catch (error) {
      fail("Add Judge", error, "Couldn't add the judge.");
    }
  };

  const editJudge = async (showId: string, ring: string, judgeIndex: number, name: string, number: string) => {
    const show = findShow(showId);
    const judge = show && ringOf(show, ring)?.running_order?.[judgeIndex];
    if (!show || !judge) return;
    const collision = (ringOf(show, ring)?.running_order || []).some((item, index) =>
      index !== judgeIndex && item.judge_name.trim().toLowerCase() === name.trim().toLowerCase()
    );
    if (collision && !window.confirm(`${name} is already in ${ring}. Saving will merge the two judge blocks. Continue?`)) {
      return;
    }
    try {
      const updated = await apiService.editRunningOrderJudge(event._id, showId, ring, {
        old_judge_name: judge.judge_name,
        old_judge_number: judge.judge_number || "",
        judge_name: name,
        judge_number: number,
      });
      setShows((prev) => prev.map((item) => item._id === showId ? updated : item));
      notify(updated);
      closeForm();
    } catch (error) {
      fail("Edit Judge", error, "Couldn't update that judge.");
    }
  };

  const editClass = async (showId: string, ring: string, judgeIndex: number, classIndex: number, values: IEditRunningOrderDogClassValues) => {
    const show = findShow(showId);
    const order = show ? ringOf(show, ring)?.running_order || [] : [];
    const tile = order[judgeIndex]?.dog_classes?.[classIndex];
    if (!show || !tile) return;
    const payload: RunningOrderDogClassDto = {
      type: tile.type,
      duration: values.duration,
      show_element: tile.show_element,
      level: tile.level,
    };
    if (values.timeLimit) payload.time_limit = values.timeLimit;
    if (values.numHides) payload.num_hides = values.numHides;
    const [search1, search2, search3] = values.searches ?? [];
    if (search1?.timeLimit) payload.time_limit_search_1 = search1.timeLimit;
    if (search1?.numHides) payload.num_hides_search_1 = search1.numHides;
    if (search2?.timeLimit) payload.time_limit_search_2 = search2.timeLimit;
    if (search2?.numHides) payload.num_hides_search_2 = search2.numHides;
    if (search3?.timeLimit) payload.time_limit_search_3 = search3.timeLimit;
    if (search3?.numHides) payload.num_hides_search_3 = search3.numHides;
    try {
      await apiService.updateShowRunningOrderDogClass(event._id, showId, ring, order[judgeIndex].judge_name, payload);
      const next = order.map((judge, index) => index !== judgeIndex ? judge : {
        ...judge,
        dog_classes: judge.dog_classes.map((dogClass, dogIndex) => {
          if (dogIndex !== classIndex) return dogClass;
          return {
            ...dogClass,
            duration: values.duration,
            total_minutes: dogClass.entries ? values.duration * dogClass.entries : dogClass.total_minutes,
            time_limit: values.timeLimit || dogClass.time_limit,
            num_hides: values.numHides || dogClass.num_hides,
          };
        }),
      });
      await saveRing(show, ring, next);
      closeForm();
    } catch (error) {
      fail("Edit Class", error, "Couldn't update the class.");
    }
  };

  const runPicked = (showId: string, ring: string) => {
    if (!form || form.kind !== "pick") return;
    // Stay in the open dialog. Closing it first toggles the modal shut, and the
    // judge form never appears because the open check still sees it as shown.
    if (form.purpose === "addJudge") {
      setForm({ kind: "addJudge", showId, ring });
      return;
    }
    closeForm();
    if (form.purpose === "addRing") addRing(showId);
    if (form.purpose === "reverse") {
      const show = findShow(showId);
      const order = show && ringOf(show, ring)?.running_order;
      if (show && order) saveRing(show, ring, reverseRing(order));
    }
    if (form.purpose === "reset") resetShow(showId);
  };

  const resetShow = async (showId: string) => {
    const show = findShow(showId) || anchorShow;
    const hasSplits = (show.rings || []).some((ring) => (ring.running_order || []).some((judge) =>
      (judge.dog_classes || []).some((dogClass) => dogClass.split_number != null)
    ));
    const message = hasSplits
      ? "Resetting the judge schedule will remove all class splits, custom breaks, and manual ordering changes. Continue?"
      : "Resetting the judge schedule can overwrite custom breaks and manual class ordering changes. Continue?";
    if (!window.confirm(message)) return;
    try {
      const updated = await apiService.syncShowRingsWithScores(event._id, showId);
      setShows((prev) => prev.map((item) => item._id === showId ? updated : item));
      notify(updated);
      toast.show({ title: "Reset", content: "The schedule was rebuilt from entries.", duration: 4000, type: "success" });
    } catch (error) {
      fail("Reset", error, "Couldn't reset the schedule.");
    }
  };

  const confirmGroup = async () => {
    const selected = model.resources.filter((resource) => groupedIds.includes(resource.id) && resource.extendedProps.kind === "class");
    const judgeIds = new Set(selected.map((resource) => `${resource.extendedProps.showId}:${resource.extendedProps.ring}:${resource.extendedProps.judgeIndex}`));
    if (judgeIds.size !== 1 || selected.length < 2) {
      toast.show({ title: "Group Levels", content: "Select at least 2 classes under the same judge.", duration: 5000, type: "fail" });
      return;
    }
    const first = selected[0];
    const show = findShow(first.extendedProps.showId || "");
    const order = show && ringOf(show, first.extendedProps.ring || "")?.running_order;
    if (!show || !order || first.extendedProps.judgeIndex == null) return;
    const indexes = selected.map((resource) => resource.extendedProps.classIndex).filter((index): index is number => index != null);
    const grouped = groupClasses(order, first.extendedProps.judgeIndex, indexes);
    if (grouped.error) {
      toast.show({ title: "Group Levels", content: grouped.error, duration: 5000, type: "fail" });
      return;
    }
    await saveRing(show, first.extendedProps.ring || "", grouped.order);
    setGrouping(false);
    setGroupedIds([]);
  };

  const moveClassTo = async (showId: string, ring: string, judgeIndex: number, classIndex: number, targetRing: string, judgeName: string) => {
    const show = findShow(showId);
    if (!show) return;
    const sourceOrder = ringOf(show, ring)?.running_order || [];
    const destOrder = ringOf(show, targetRing)?.running_order || [];
    const destJudge = destOrder.findIndex((judge) => judge.judge_name === judgeName);
    if (destJudge < 0) return;
    const transferred = transferClassToRing(sourceOrder, judgeIndex, classIndex, destOrder, destJudge, destOrder[destJudge].dog_classes.length, show._id);
    if ("ok" in transferred && transferred.ok === false) {
      refuseMove(transferred.reason);
      return;
    }
    if (!("source" in transferred)) return;
    try {
      await apiService.updateRingRunningOrder(event._id, show._id, ring, transferred.source);
      await apiService.updateRingRunningOrder(event._id, show._id, targetRing, transferred.dest);
      const next = replaceRingOrder(replaceRingOrder(show, ring, transferred.source), targetRing, transferred.dest);
      setShows((prev) => prev.map((item) => item._id === show._id ? next : item));
      notify(next);
      applyEffects(show._id, transferred.effects);
      closeForm();
    } catch (error) {
      fail("Move Class", error, "Couldn't move that class.");
    }
  };

  const selectedResource = model.resources.find((resource) => resource.id === selectedJudgeId);
  const selectedShow = selectedResource ? findShow(selectedResource.extendedProps.showId) : undefined;
  const selectedJudge = selectedShow && selectedResource?.extendedProps.judgeIndex != null
    ? ringOf(selectedShow, selectedResource.extendedProps.ring || "")?.running_order?.[selectedResource.extendedProps.judgeIndex]
    : undefined;
  const dailyMinutes = selectedJudge ? shows.filter((item) => sameLocalDay(item.show_date, anchor.show_date)).reduce((sum, item) => {
    const timeline = buildScheduleModel([item], shows, event, false).timelines.get(item._id);
    const minutes = timeline?.rings.flatMap((ring) => ring.judges)
      .filter((judge) => judge.judgeName.trim() === selectedJudge.judge_name.trim())
      .reduce((judgeSum, judge) => judgeSum + judge.judgingMinutes, 0) || 0;
    return sum + minutes;
  }, 0) : 0;
  const trialMinutes = selectedResource?.extendedProps.kind === "judge" ? selectedResource.total : "";

  const moveListRow = async (fromId: string, toId: string) => {
    if (!fromId || fromId === toId) return;
    const fromResource = model.resources.find((item) => item.id === fromId);
    const targetResource = model.resources.find((item) => item.id === toId);
    if (!fromResource || !targetResource) return;
    const from = fromResource.extendedProps;
    const target = targetResource.extendedProps;
    if (from.kind !== "class" && from.kind !== "break") return;
    if (target.kind !== "judge" && target.kind !== "class" && target.kind !== "break") return;
    const show = findShow(from.showId);
    if (!show || from.judgeIndex == null || from.classIndex == null) return;
    const sourceOrder = ringOf(show, from.ring || "")?.running_order || [];
    const onClassRow = (target.kind === "class" || target.kind === "break") && target.classIndex != null;

    if (concurrent && from.judgeKey) {
      const [judgeName, judgeNumber] = String(from.judgeKey).split("|");
      const sameMergedJudge = from.judgeKey === target.judgeKey && from.ring === target.ring;
      if (sameMergedJudge && onClassRow) {
        const reordered = reorderConcurrentClass(visibleShows, from.ring || "", judgeName, judgeNumber, {
          showId: from.showId,
          classIndex: from.classIndex,
        }, {
          showId: target.showId,
          classIndex: target.classIndex as number,
        });
        if (reordered) await saveShows(shows.map((item) => reordered.find((next) => next._id === item._id) || item));
        return;
      }
      if (!sameMergedJudge && target.judgeKey && target.ring && from.kind === "class") {
        const reassigned = reassignConcurrentClass(visibleShows, {
          showId: from.showId,
          ring: from.ring || "",
          judgeIndex: from.judgeIndex,
          classIndex: from.classIndex,
        }, {
          ring: target.ring,
          judgeKey: target.judgeKey,
        }, onClassRow ? { showId: target.showId, classIndex: target.classIndex as number } : undefined);
        if (!reassigned.ok) {
          if (reassigned.reason === "ring") {
            toast.show({ title: "Move Class", content: "That trial does not use this ring.", duration: 6000, type: "fail" });
          } else if (reassigned.reason !== "missing") {
            refuseMove(reassigned.reason);
          }
          return;
        }
        const saved = await saveShows(shows.map((item) => reassigned.shows.find((next) => next._id === item._id) || item));
        if (saved) reassigned.effects.forEach((effect) => applyEffects(reassigned.showId, effect));
      } else if (!sameMergedJudge && from.kind === "break") {
        refuseMove("break");
      }
      return;
    }

    const sameJudge = target.ring === from.ring && target.judgeIndex === from.judgeIndex && target.showId === from.showId;
    if (sameJudge && onClassRow) {
      const reordered = reorderClassToRow(sourceOrder, from.judgeIndex, from.classIndex, target.classIndex as number);
      if (reordered) await saveRing(show, from.ring || "", reordered);
      return;
    }
    if (from.kind === "break") {
      refuseMove("break");
      return;
    }
    const destRing = target.ring;
    const destJudge = target.judgeIndex;
    if (!destRing || destJudge == null || target.showId !== from.showId) return;
    const insertAt = onClassRow
      ? target.classIndex as number
      : (ringOf(show, destRing)?.running_order?.[destJudge]?.dog_classes.length || 0);
    if (destRing === from.ring) {
      const plan = planClassMove(sourceOrder, from.judgeIndex, from.classIndex, destJudge, insertAt, show._id);
      if (!plan.ok) {
        refuseMove(plan.reason);
        return;
      }
      await saveRing(show, from.ring || "", plan.runningOrder);
      applyEffects(show._id, plan.effects);
      return;
    }
    const destOrder = ringOf(show, destRing)?.running_order || [];
    const transferred = transferClassToRing(sourceOrder, from.judgeIndex, from.classIndex, destOrder, destJudge, insertAt, show._id);
    if ("ok" in transferred && transferred.ok === false) {
      refuseMove(transferred.reason);
      return;
    }
    if (!("source" in transferred)) return;
    setSaving(true);
    try {
      await apiService.updateRingRunningOrder(event._id, show._id, from.ring || "", transferred.source);
      await apiService.updateRingRunningOrder(event._id, show._id, destRing, transferred.dest);
      const next = replaceRingOrder(replaceRingOrder(show, from.ring || "", transferred.source), destRing, transferred.dest);
      setShows((prev) => prev.map((item) => item._id === show._id ? next : item));
      notify(next);
      applyEffects(show._id, transferred.effects);
    } catch (error) {
      fail("Move Class", error, "Couldn't move that class.");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const moveListRowRef = useRef(moveListRow);
  moveListRowRef.current = moveListRow;

  useEffect(() => {
    if (grouping || narrow || loading) return;
    const root = document.querySelector(".jsb-calendar");
    const body = root?.querySelector(".fc-datagrid-body");
    if (!body) return;

    const rowOf = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el?.closest) return null;
      const cell = (el.closest("td.fc-datagrid-cell[data-resource-id]")
        || el.querySelector?.("td.fc-datagrid-cell[data-resource-id]")) as HTMLElement | null;
      if (!cell || !body.contains(cell)) return null;
      const id = cell.getAttribute("data-resource-id") || "";
      const row = cell.closest("tr") as HTMLElement | null;
      if (!id || !row) return null;
      return { id, row };
    };
    const kindOf = (id: string) => model.resources.find((item) => item.id === id)?.extendedProps.kind;
    const canDrag = (id: string) => {
      const kind = kindOf(id);
      return kind === "class" || kind === "break";
    };
    const canDrop = (id: string) => {
      const kind = kindOf(id);
      return kind === "class" || kind === "break" || kind === "judge";
    };

    let pressTarget: HTMLElement | null = null;
    let dragging = false;
    const markRows = () => {
      body.querySelectorAll("td.fc-datagrid-cell[data-resource-id]").forEach((cell) => {
        const id = cell.getAttribute("data-resource-id") || "";
        (cell as HTMLElement).draggable = canDrag(id);
      });
    };
    markRows();
    const observer = new MutationObserver(markRows);
    observer.observe(body, { childList: true, subtree: true });

    const onMouseDown = (event: Event) => {
      pressTarget = event.target as HTMLElement;
    };
    const onDragStart = (event: Event) => {
      const drag = event as DragEvent;
      const hit = rowOf(drag.target);
      if (!hit || !canDrag(hit.id) || pressTarget?.closest("button")) {
        drag.preventDefault();
        return;
      }
      dragging = true;
      drag.dataTransfer?.setData("text/plain", `jsb:${hit.id}`);
      if (drag.dataTransfer) drag.dataTransfer.effectAllowed = "move";
    };
    const onDragEnd = () => {
      dragging = false;
      body.querySelectorAll(`.${styles.dropOver}`).forEach((row) => row.classList.remove(styles.dropOver));
    };
    const onDragOver = (event: Event) => {
      const drag = event as DragEvent;
      if (!dragging) return;
      const hit = rowOf(drag.target);
      if (!hit || !canDrop(hit.id)) return;
      drag.preventDefault();
      hit.row.classList.add(styles.dropOver);
    };
    const onDragLeave = (event: Event) => {
      const drag = event as DragEvent;
      const hit = rowOf(drag.target);
      if (!hit) return;
      const next = drag.relatedTarget as Node | null;
      if (next && hit.row.contains(next)) return;
      hit.row.classList.remove(styles.dropOver);
    };
    const onDrop = (event: Event) => {
      const drag = event as DragEvent;
      const hit = rowOf(drag.target);
      if (!hit) return;
      drag.preventDefault();
      hit.row.classList.remove(styles.dropOver);
      const token = drag.dataTransfer?.getData("text/plain") || "";
      if (token.startsWith("jsb:")) moveListRowRef.current(token.slice(4), hit.id);
    };

    body.addEventListener("mousedown", onMouseDown);
    body.addEventListener("dragstart", onDragStart);
    body.addEventListener("dragend", onDragEnd);
    body.addEventListener("dragover", onDragOver);
    body.addEventListener("dragleave", onDragLeave);
    body.addEventListener("drop", onDrop);
    return () => {
      observer.disconnect();
      body.removeEventListener("mousedown", onMouseDown);
      body.removeEventListener("dragstart", onDragStart);
      body.removeEventListener("dragend", onDragEnd);
      body.removeEventListener("dragover", onDragOver);
      body.removeEventListener("dragleave", onDragLeave);
      body.removeEventListener("drop", onDrop);
    };
  }, [grouping, narrow, loading, model.resources, styles.dropOver]);

  const resourceLabel = (arg: any) => {
    const resource = model.resources.find((item) => item.id === arg.resource.id);
    if (!resource) return arg.resource.title;
    const props = resource.extendedProps;
    const onOpen = () => setSelectedJudgeId(resource.id);
    return (
      <span className={styles.rowLabel} onClick={onOpen}>
        {grouping && props.kind === "class" && (
          <input
            type="checkbox"
            checked={groupedIds.includes(resource.id)}
            onChange={() => setGroupedIds((prev) => prev.includes(resource.id) ? prev.filter((id) => id !== resource.id) : [...prev, resource.id])}
          />
        )}
        <span className={`${styles.rowTitle}${props.conflict ? ` ${styles.conflictName}` : ""}`}>{resource.title}</span>
        {props.grouped ? <span title="Grouped classes"> ⛓</span> : null}
        {props.kind === "ring" && props.canDeleteRing && (
          <button type="button" className={styles.linkButton} onClick={() => deleteRing(props.showId, props.ring || "")}>Delete</button>
        )}
        {(props.kind === "judge" || props.kind === "class" || props.kind === "break") && !grouping && (
          <span className={styles.rowActions}>
            <button
              type="button"
              className={styles.moreButton}
              title="More"
              draggable={false}
              onClick={(click) => {
                click.stopPropagation();
                if (rowMenu?.resourceId === resource.id) {
                  setRowMenu(null);
                  return;
                }
                const rect = click.currentTarget.getBoundingClientRect();
                setRowMenu({ x: rect.right, y: rect.bottom + 4, resourceId: resource.id, place: "button" });
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <circle cx="3.5" cy="7" r="1.1" fill="currentColor" />
                <circle cx="7" cy="7" r="1.1" fill="currentColor" />
                <circle cx="10.5" cy="7" r="1.1" fill="currentColor" />
              </svg>
            </button>
          </span>
        )}
      </span>
    );
  };

  const editResource = (resource: ScheduleResource) => {
    const { showId, ring = "", judgeIndex = 0, classIndex = 0, kind } = resource.extendedProps;
    if (kind === "judge") {
      openForm({ kind: "editJudge", showId, ring, judgeIndex });
      return;
    }
    if (kind === "class") {
      openForm({ kind: "editClass", showId, ring, judgeIndex, classIndex });
      return;
    }
    if (kind === "break") {
      openForm({ kind: "editBreak", showId, ring, judgeIndex, classIndex });
    }
  };

  const deleteResource = (resource: ScheduleResource) => {
    const { showId, ring = "", judgeIndex = 0, classIndex = 0, kind } = resource.extendedProps;
    const show = findShow(showId);
    if (!show) return;
    const order = ringOf(show, ring)?.running_order || [];
    if (kind === "judge") {
      const result = deleteJudgeTile(order, judgeIndex);
      if (result.error && result.error !== "missing") {
        toast.show({ title: "Delete Judge", content: result.error, duration: 5000, type: "fail" });
        return;
      }
      const judge = order[judgeIndex];
      if (!judge) return;
      const name = `${judge.judge_name}${judge.judge_number ? ` (${judge.judge_number})` : ""}`;
      setPendingDelete({
        message: `Are you sure you want to delete ${name}?`,
        run: () => saveRing(show, ring, result.order),
      });
      return;
    }
    const tile = order[judgeIndex]?.dog_classes?.[classIndex];
    if (tile?.bis_class) {
      setPendingDelete({
        message: `Remove ${tile.bis_class} from this show? You can add it back from Add Break.`,
        run: () => hideBisBlock(show, ring, judgeIndex, classIndex),
      });
      return;
    }
    const label = kind === "break" ? (tile?.name || "this break") : resource.title;
    setPendingDelete({
      message: `Remove ${label}?`,
      run: () => saveRing(show, ring, removeTile(order, judgeIndex, classIndex)),
    });
  };

  const menuClass = (resource?: ScheduleResource) => {
    if (!resource || resource.extendedProps.kind !== "class") return undefined;
    const { showId, ring = "", judgeIndex, classIndex } = resource.extendedProps;
    if (judgeIndex == null || classIndex == null) return undefined;
    const show = findShow(showId);
    return show ? tileAt(show, ring, judgeIndex, classIndex) : undefined;
  };

  const splitResource = (resource: ScheduleResource) => {
    const { showId, ring = "", judgeIndex, classIndex } = resource.extendedProps;
    if (judgeIndex == null || classIndex == null) return;
    const show = findShow(showId);
    if (!show) return;
    const order = ringOf(show, ring)?.running_order || [];
    const result = splitClassTile(order, judgeIndex, classIndex);
    if (result.error) {
      if (result.error !== "missing") {
        toast.show({ title: "Split Class", content: result.error, duration: 5000, type: "fail" });
      }
      return;
    }
    saveRing(show, ring, result.order);
  };

  const mergeResource = (resource: ScheduleResource) => {
    const { showId, ring = "", judgeIndex, classIndex } = resource.extendedProps;
    if (judgeIndex == null || classIndex == null) return;
    const show = findShow(showId);
    const tile = show && tileAt(show, ring, judgeIndex, classIndex);
    if (!show || !tile) return;
    if (tile.dog_run_order && tile.dog_run_order.length > 0) {
      openForm({ kind: "merge", showId, ring, judgeIndex, classIndex });
      return;
    }
    const order = ringOf(show, ring)?.running_order || [];
    saveRing(show, ring, mergeClassSplits(order, judgeIndex, classIndex));
  };

  const onTimelineContextMenu = (click: React.MouseEvent) => {
    if (grouping) return;
    const target = click.target as HTMLElement;
    const root = target.closest(".jsb-calendar");
    if (!root || !target.closest(".fc-timeline-body")) return;
    const lanes = Array.from(root.querySelectorAll("td.fc-timeline-lane[data-resource-id]"));
    const lane = lanes.find((item) => {
      const rect = item.getBoundingClientRect();
      return click.clientY >= rect.top && click.clientY <= rect.bottom;
    });
    const classEvent = target.closest(".jsb-class") as HTMLElement | null;
    if (classEvent) {
      const classResourceId = classEvent.getAttribute("data-resource-id") || lane?.getAttribute("data-resource-id") || "";
      if (!classResourceId) return;
      click.preventDefault();
      setGridMenu(null);
      setRowMenu({ x: click.clientX, y: click.clientY, resourceId: classResourceId, place: "cursor" });
      return;
    }
    const breakEvent = target.closest(".jsb-break");
    const resourceId = breakEvent?.getAttribute("data-resource-id") || lane?.getAttribute("data-resource-id");
    const resource = model.resources.find((item) => item.id === resourceId);
    const kind = resource?.extendedProps.kind;
    if (!resource || (kind !== "judge" && kind !== "class" && kind !== "break")) return;
    const slot = slotAtX(root, click.clientX);
    const show = findShow(resource.extendedProps.showId);
    const when = slot ? clickTimeMs(slot, click.clientX, timezone) : null;
    if (!show || when == null || resource.extendedProps.judgeIndex == null) return;
    click.preventDefault();
    setRowMenu(null);
    const atIndex = insertionIndexFromDrop(
      visibleTiles(show, resource.extendedProps.ring || "", resource.extendedProps.judgeIndex),
      when,
    );
    setGridMenu({
      x: click.clientX,
      y: click.clientY,
      showId: resource.extendedProps.showId,
      ring: resource.extendedProps.ring || "",
      judgeIndex: resource.extendedProps.judgeIndex,
      atIndex,
      breakClassIndex: kind === "break" ? resource.extendedProps.classIndex : undefined,
      bisClass: kind === "break" && resource.extendedProps.classIndex != null
        ? tileAt(show, resource.extendedProps.ring || "", resource.extendedProps.judgeIndex, resource.extendedProps.classIndex)?.bis_class
        : undefined,
    });
  };

  const addBreakFromMenu = () => {
    if (!gridMenu) return;
    const menu = gridMenu;
    setGridMenu(null);
    openForm({ kind: "addBreak", showId: menu.showId, ring: menu.ring, judgeIndex: menu.judgeIndex, atIndex: menu.atIndex });
  };

  const editBreakFromMenu = () => {
    const classIndex = gridMenu?.breakClassIndex;
    if (!gridMenu || classIndex == null) return;
    const { showId, ring, judgeIndex } = gridMenu;
    setGridMenu(null);
    openForm({ kind: "editBreak", showId, ring, judgeIndex, classIndex });
  };

  const deleteBreakFromMenu = () => {
    const classIndex = gridMenu?.breakClassIndex;
    if (!gridMenu || classIndex == null) return;
    const { showId, ring, judgeIndex } = gridMenu;
    setGridMenu(null);
    const show = findShow(showId);
    if (!show) return;
    const order = ringOf(show, ring)?.running_order || [];
    const tile = order[judgeIndex]?.dog_classes?.[classIndex];
    if (tile?.bis_class) {
      setPendingDelete({
        message: `Remove ${tile.bis_class} from this show? You can add it back from Add Break.`,
        run: () => hideBisBlock(show, ring, judgeIndex, classIndex),
      });
      return;
    }
    setPendingDelete({
      message: `Remove ${tile?.name || "this break"}?`,
      run: () => saveRing(show, ring, removeTile(order, judgeIndex, classIndex)),
    });
  };

  const formBody = () => {
    if (!form) return <div />;
    if (form.kind === "moveToJudge") {
      const from = model.resources.find((item) => item.id === form.resourceId);
      return (
        <MoveToJudgePicker
          judges={from ? moveJudgeChoices(from) : []}
          onCancel={closeForm}
          onPick={(judgeResourceId) => {
            closeForm();
            moveListRow(form.resourceId, judgeResourceId);
          }}
        />
      );
    }
    if (form.kind === "pick") {
      return (
        <RingTrialPicker
          title={form.purpose === "reset" || form.purpose === "addRing" ? "Which trial?" : "Which ring?"}
          rings={form.purpose === "addJudge" ? judgeRings() : form.purpose === "addRing" ? pickerTrials() : pickerRings()}
          ringOnly={form.purpose === "addJudge" && concurrent}
          trialOnly={form.purpose === "addRing"}
          onPick={runPicked}
          onCancel={closeForm}
        />
      );
    }
    if (form.kind === "addJudge" || form.kind === "editJudge") {
      const judge = form.kind === "editJudge" ? findShow(form.showId) && ringOf(findShow(form.showId) as IShow, form.ring)?.running_order?.[form.judgeIndex] : undefined;
      return (
        <AddRunningOrderJudge
          event={event}
          title={form.kind === "editJudge" ? "Edit Judge" : "Add Judge"}
          initialName={judge?.judge_name || ""}
          initialNumber={judge?.judge_number || ""}
          onSubmit={(name, number) => form.kind === "editJudge"
            ? editJudge(form.showId, form.ring, form.judgeIndex, name, number)
            : addJudge(form.showId, form.ring, name, number)}
        />
      );
    }
    if (form.kind === "editClass") {
      const show = findShow(form.showId);
      const tile = show && tileAt(show, form.ring, form.judgeIndex, form.classIndex);
      if (!show || !tile) return <div />;
      return <EditRunningOrderDogClass event={event} show={show} dogClass={tile} onSubmit={(values) => editClass(form.showId, form.ring, form.judgeIndex, form.classIndex, values)} />;
    }
    if (form.kind === "moveClass") {
      const show = findShow(form.showId);
      const judge = show && ringOf(show, form.ring)?.running_order?.[form.judgeIndex];
      if (!show || !judge) return <div />;
      return (
        <MoveClassToRing
          currentRing={form.ring}
          currentJudgeName={judge.judge_name}
          show={show}
          ringOptions={(show.rings || []).map((ring) => ({ id: ring.ring, value: ring.ring }))}
          onFinish={(targetRing, judgeName) => moveClassTo(form.showId, form.ring, form.judgeIndex, form.classIndex, targetRing, judgeName)}
        />
      );
    }
    if (form.kind === "moveJudge") {
      const resource = model.resources.find((item) => item.id === form.resourceId);
      const rings = resource ? moveRingChoices(resource) : [];
      return (
        <RingTrialPicker
          title="Move to ring"
          rings={rings}
          ringOnly
          confirmLabel="Move"
          onCancel={closeForm}
          onPick={(_showId, targetRing) => {
            closeForm();
            if (resource) finishMoveJudge(resource, targetRing);
          }}
        />
      );
    }
    if (form.kind === "addBreak" || form.kind === "editBreak") {
      const show = findShow(form.showId);
      const tile = form.kind === "editBreak" && show ? tileAt(show, form.ring, form.judgeIndex, form.classIndex) : undefined;
      const hiddenBlocks = form.kind === "addBreak" && show && isUKCConformationShow(event.sanctioning_club, show.show_type)
        ? (show.hidden_bis_classes || [])
        : [];
      return (
        <BreakForm
          title={tile?.bis_class ? `Edit ${tile.bis_class}` : form.kind === "editBreak" ? "Edit Break" : "Add Break"}
          initialName={tile?.name || "Break"}
          fixedName={isUKCConformationBISTile(tile)}
          initialDuration={tile?.duration || 60}
          restoreOptions={hiddenBlocks}
          onRestore={(bisClass) => {
            if (!show || form.kind !== "addBreak") return;
            restoreBisBlock(show, form.ring, form.judgeIndex, form.atIndex, bisClass);
            closeForm();
          }}
          onCancel={closeForm}
          onSubmit={(name, duration) => {
            if (!show) return;
            const order = ringOf(show, form.ring)?.running_order || [];
            const next = form.kind === "editBreak"
              ? updateBreakTile(order, form.judgeIndex, form.classIndex, { name, duration })
              : addBreakTile(order, form.judgeIndex, form.atIndex, { name, duration });
            saveRing(show, form.ring, next);
            closeForm();
          }}
        />
      );
    }
    if (form.kind === "merge") {
      const show = findShow(form.showId);
      return (
        <div className={styles.form}>
          <h3>Merge splits?</h3>
          <p>Dogs already assigned in these splits will be combined back into one class.</p>
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
            <Button onClick={() => {
              if (!show) return;
              const order = ringOf(show, form.ring)?.running_order || [];
              saveRing(show, form.ring, mergeClassSplits(order, form.judgeIndex, form.classIndex));
              closeForm();
            }}>Merge</Button>
          </div>
        </div>
      );
    }
    return <div />;
  };

  const stepDate = (direction: -1 | 1) => {
    const index = dates.indexOf(viewDate);
    const next = dates[index + direction];
    if (next) setViewDate(next);
  };

  return (
    <>
      <Modal className="judgeScheduleBuilder" isShown hide={onClose} modalContent={
        <div className={styles.shell}>
          <header className={styles.header}>
            <div>
              <h2>Advanced Judge Schedule Builder</h2>
            </div>
          </header>
          {!narrow && (
          <div className={styles.toolbar}>
            <div className={styles.dateNav}>
              <button type="button" aria-label="Previous day" onClick={() => stepDate(-1)} disabled={dates.indexOf(viewDate) <= 0}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m14.5 5-7 7 7 7" /></svg>
              </button>
              <div className={styles.dateLabel}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 3v3m10-3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13H4V6a1 1 0 0 1 1-1Z" /></svg>
                {moment(viewDate).format("MMM D, YYYY")}
              </div>
              <button type="button" aria-label="Next day" onClick={() => stepDate(1)} disabled={dates.indexOf(viewDate) >= dates.length - 1}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m9.5 5 7 7-7 7" /></svg>
              </button>
            </div>
            <Button className={styles.action} onClick={() => concurrent ? addConcurrentRing() : withRing("addRing", (showId) => addRing(showId))}>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
              Add Ring
            </Button>
            <Button className={styles.action} onClick={() => withRing("addJudge", (showId, ring) => openForm({ kind: "addJudge", showId, ring }))}>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="8" r="3" /><path d="M4.5 19c.7-3.8 2.5-5.5 5.5-5.5s4.8 1.7 5.5 5.5M18 8v6m-3-3h6" /></svg>
              Add Judge
            </Button>
            <Button className={styles.action} onClick={() => withRing("reset", (showId) => resetShow(showId))}>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 7v5h5M6 11a7 7 0 1 1 1.5 6.5" /></svg>
              Reset
            </Button>
            {!grouping ? (
              <Button className={styles.action} onClick={() => { setGrouping(true); setGroupedIds([]); }}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 7h14M5 12h14M5 17h14M8 4v6m8 4v6" /></svg>
                Group Levels
              </Button>
            ) : (
              <>
                <span className={styles.groupHint}>{groupedIds.length} selected</span>
                <Button className={styles.action} onClick={confirmGroup} disabled={groupedIds.length < 2}>Confirm Group</Button>
                <Button variant="secondary" onClick={() => { setGrouping(false); setGroupedIds([]); }}>Cancel</Button>
              </>
            )}
            {!concurrent && (
              <Button className={styles.action} onClick={() => withRing("reverse", (showId, ring) => {
                const show = findShow(showId);
                const order = show && ringOf(show, ring)?.running_order;
                if (show && order) saveRing(show, ring, reverseRing(order));
              })}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 5v14m0 0-3-3m3 3 3-3M17 19V5m0 0-3 3m3-3 3 3" /></svg>
                Reverse Order
              </Button>
            )}
            <div className={styles.checks}>
            <Checkbox
              id="jsb-hide-empty"
              label="Hide empty"
              value={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
            />
            <Checkbox
              id="jsb-concurrent"
              label="Concurrent Schedule"
              value={concurrent}
              onChange={async (e) => {
                const value = e.target.checked;
                setConcurrent(value);
                try {
                  const { __v, ...rest } = event as IEvent & { __v?: number };
                  await apiService.updateEvent(event._id, { ...rest, concurrent_schedule: value } as any);
                  if (value) {
                    const assigned = assignMissingDayOrders(shows, event.timezone);
                    await saveShows(assigned);
                  }
                } catch (error) {
                  setConcurrent(!value);
                  fail("Judge Schedule", error, "Couldn't update Concurrent Schedule.");
                }
              }}
            />
            <Checkbox
              id="jsb-round-to-5"
              label="Round to 5"
              value={!!anchorShow.round_start_times_to_5}
              onChange={async (e) => {
                const value = e.target.checked;
                const { __v, updated_at, created_at, ...rest } = anchorShow as IShow & { __v?: number; updated_at?: string; created_at?: string };
                try {
                  const updated = await apiService.updateShow(event._id, anchor._id, { ...rest, round_start_times_to_5: value } as any);
                  const next = { ...anchorShow, ...updated, round_start_times_to_5: value };
                  setShows((prev) => prev.map((item) => item._id === anchor._id ? next : item));
                  notify(next);
                } catch (error) {
                  fail("Judge Schedule", error, "Couldn't update rounding.");
                }
              }}
            />
            {saving && <span className={styles.saving}>Saving…</span>}
            </div>
          </div>
          )}
          {narrow ? (
            <div className={styles.narrow}>
              <p>The schedule builder needs a wider window. Use the Manage option on each trial to edit the schedule.</p>
            </div>
          ) : loading ? (
            <div className={styles.loading}><p>Loading schedule…</p></div>
          ) : loadError ? (
            <div className={styles.narrow}><p>{loadError}</p></div>
          ) : (
            <div className={`${styles.calendar} jsb-calendar`} onContextMenu={onTimelineContextMenu}>
              <FullCalendar
                ref={calendarRef}
                plugins={[resourceTimelinePlugin, interactionPlugin, momentTimezonePlugin]}
                schedulerLicenseKey={Config.web.REACT_APP_FULLCALENDAR_LICENSE_KEY || ""}
                initialView="resourceTimelineDay"
                initialDate={viewDate}
                timeZone={timezone}
                headerToolbar={false}
                resourceAreaWidth={concurrent ? "528px" : "418px"}
                resourceAreaColumns={[
                  { field: "title", headerContent: concurrent ? "Ring / Judge / Class" : "Trial / Ring / Judge / Class", width: concurrent ? 380 : 270, cellClassNames: rowCellClassNames },
                  { field: "entries", headerContent: "Entries", width: 64, cellClassNames: rowCellClassNames },
                  { field: "estStart", headerContent: "Start time", width: 84, cellClassNames: rowCellClassNames },
                ]}
                resourceOrder="order"
                resources={model.resources}
                events={model.events}
                resourceLabelContent={resourceLabel}
                slotDuration="00:15:00"
                slotLabelInterval="00:15:00"
                snapDuration="00:05:00"
                slotMinWidth={60}
                eventMinWidth={0}
                slotMinTime={model.slotMinTime}
                slotMaxTime={model.slotMaxTime}
                height="100%"
                nowIndicator={viewDate === moment.tz(timezone).format("YYYY-MM-DD")}
                editable={!grouping}
                eventResourceEditable={!grouping}
                eventDurationEditable={!grouping}
                eventResizableFromStart={false}
                eventOverlap
                eventAllow={onEventAllow}
                eventDrop={onEventDrop}
                eventResize={onEventResize}
                eventDidMount={(info) => {
                  const tip = info.event.extendedProps.tooltip;
                  if (tip) info.el.setAttribute("title", tip);
                  const props = info.event.extendedProps;
                  if (props.kind === "break" || props.kind === "class") {
                    info.el.setAttribute("data-resource-id", info.event.getResources()[0]?.id || "");
                  }
                  window.requestAnimationFrame(() => placeOutsideLabel(info.el));
                }}
                eventClick={(info) => {
                  if (info.jsEvent.detail === 2 && info.event.extendedProps.kind === "class") {
                    const props = info.event.extendedProps;
                    openForm({ kind: "editClass", showId: props.showId, ring: props.ring, judgeIndex: props.judgeIndex, classIndex: props.classIndex });
                  }
                }}
              />
            </div>
          )}
          {!narrow && (
          <footer className={styles.footer}>
            {selectedJudge ? (
              <span>
                {selectedJudge.judge_name} · {moment(anchor.show_date).format("MMM Do")} · All Shows/Trials {formatMinutes(dailyMinutes)}
                {trialMinutes ? ` · ${anchorShow.show_name} ${trialMinutes}` : ""}
              </span>
            ) : (
              <span>Select a judge to see the day total.</span>
            )}
            {model.conflictCount > 0 && <span className={styles.conflictName}>⚠ {model.conflictCount} conflict{model.conflictCount === 1 ? "" : "s"}</span>}
          </footer>
          )}
        </div>
      } />
      <Modal
        className={`judgeScheduleBuilderForm${form?.kind === "addBreak" || form?.kind === "editBreak" ? " judgeScheduleBreakForm" : ""}${form?.kind === "pick" || form?.kind === "moveToJudge" || form?.kind === "moveJudge" || form?.kind === "merge" ? " judgeSchedulePickerForm" : ""}`}
        isShown={isShown}
        hide={closeForm}
        modalContent={formBody()}
      />
      <Modal
        className="Confirmation judgeScheduleConfirm"
        isShown={pendingDelete != null}
        hide={() => setPendingDelete(null)}
        modalContent={(
          <Confirm
            message={pendingDelete?.message || ""}
            onCancel={() => setPendingDelete(null)}
            onConfirm={() => {
              pendingDelete?.run();
              setPendingDelete(null);
            }}
          />
        )}
      />
      {gridMenu && createPortal(
        <div
          className={styles.gridMenu}
          style={{ top: gridMenu.y, left: gridMenu.x }}
          onMouseDown={(click) => click.stopPropagation()}
        >
          {gridMenu.breakClassIndex != null && (
            <>
              <button type="button" onClick={editBreakFromMenu}>{gridMenu.bisClass ? `Edit ${gridMenu.bisClass}` : "Edit Break"}</button>
              <button type="button" onClick={deleteBreakFromMenu}>{gridMenu.bisClass ? `Remove ${gridMenu.bisClass}` : "Delete Break"}</button>
            </>
          )}
          <button type="button" onClick={addBreakFromMenu}>Add Break</button>
        </div>,
        document.body,
      )}
      {rowMenu && createPortal(
        <div
          className={styles.gridMenu}
          style={{ top: rowMenu.y, left: rowMenu.x, transform: rowMenu.place === "button" ? "translateX(-100%)" : undefined }}
          onMouseDown={(click) => click.stopPropagation()}
        >
          <button type="button" onClick={() => {
            const resource = model.resources.find((item) => item.id === rowMenu.resourceId);
            setRowMenu(null);
            if (resource) editResource(resource);
          }}>Edit</button>
          {(() => {
            const resource = model.resources.find((item) => item.id === rowMenu.resourceId);
            if (!resource || resource.extendedProps.kind !== "judge" || moveRingChoices(resource).length === 0) return null;
            return (
              <button type="button" onClick={() => {
                const resourceId = rowMenu.resourceId;
                setRowMenu(null);
                openForm({ kind: "moveJudge", resourceId });
              }}>Move to ring</button>
            );
          })()}
          {classCanMove(model.resources.find((item) => item.id === rowMenu.resourceId)) && (
            <button type="button" onClick={() => {
              const resourceId = rowMenu.resourceId;
              setRowMenu(null);
              openForm({ kind: "moveToJudge", resourceId });
            }}>Move to judge</button>
          )}
          {(() => {
            const resource = model.resources.find((item) => item.id === rowMenu.resourceId);
            const tile = menuClass(resource);
            if (!resource || !tile) return null;
            const canSplit = !tile.group_with && (tile.split_total == null || tile.split_total < 4);
            const canMerge = tile.split_number != null;
            return (
              <>
                {canSplit && (
                  <button type="button" onClick={() => { setRowMenu(null); splitResource(resource); }}>Split</button>
                )}
                {canMerge && (
                  <button type="button" onClick={() => { setRowMenu(null); mergeResource(resource); }}>Merge</button>
                )}
              </>
            );
          })()}
          {model.resources.find((item) => item.id === rowMenu.resourceId)?.extendedProps.kind !== "class" && (
            <button type="button" onClick={() => {
              const resource = model.resources.find((item) => item.id === rowMenu.resourceId);
              setRowMenu(null);
              if (resource) deleteResource(resource);
            }}>Delete</button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default JudgeScheduleBuilder;
